import type { ValidationError } from './types';
import { isValidPageAttribute, isValidContainerAttribute } from './transforms';

/**
 * Valid root tag names
 */
const VALID_ROOT_TAGS = ['page', 'section'];

/**
 * Valid child tags for <page>
 */
const PAGE_CHILDREN = ['section'];

/**
 * Valid child tags for containers (section/row/column)
 * Containers can contain other containers or leaf elements
 */
const CONTAINER_CHILDREN = [
  'section',
  'row',
  'column',
  'text',
  'image',
  'button',
  'video',
  'countdown',
  'form',
];

/**
 * Check if the root element is a page element
 */
export const isPageRoot = (root: Element): boolean => {
  return root.tagName.toLowerCase() === 'page';
};

/**
 * Check if the root element is a section element
 */
export const isSectionRoot = (root: Element): boolean => {
  return root.tagName.toLowerCase() === 'section';
};

/**
 * Validate XML structure
 *
 * @param root - Root XML element
 * @returns Array of validation errors
 */
export const validateStructure = (root: Element): ValidationError[] => {
  const errors: ValidationError[] = [];
  const rootTag = root.tagName.toLowerCase();

  // Check root is either <page> or <section>
  if (!VALID_ROOT_TAGS.includes(rootTag)) {
    errors.push({
      severity: 'error',
      type: 'structural',
      message: `Root element must be <page> or <section>, found <${root.tagName}>`,
      element: root.tagName,
    });
    return errors; // Can't validate further without correct root
  }

  // Validate based on root type
  if (rootTag === 'page') {
    errors.push(...validatePageRoot(root));
  }
  // Section as root has no additional structural constraints

  return errors;
};

/**
 * Validate <page> as root element
 */
const validatePageRoot = (root: Element): ValidationError[] => {
  const errors: ValidationError[] = [];
  const children = Array.from(root.children);

  // Check for invalid direct children of <page>
  children.forEach((child) => {
    const tagName = child.tagName.toLowerCase();
    if (!PAGE_CHILDREN.includes(tagName)) {
      errors.push({
        severity: 'error',
        type: 'structural',
        message: `<page> cannot contain <${tagName}>. Only <section> elements are allowed as direct children.`,
        element: tagName,
      });
    }
  });

  return errors;
};

/**
 * Check if a tag is a valid container child
 */
export const isValidContainerChild = (tagName: string): boolean => {
  return CONTAINER_CHILDREN.includes(tagName.toLowerCase());
};

/**
 * Validate attributes on an element
 *
 * @param element - XML element to validate
 * @param validAttributes - List of valid attribute names
 * @returns Array of validation warnings for unknown attributes
 */
export const validateAttributes = (
  element: Element,
  validAttributes: readonly string[],
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const attrs = Array.from(element.attributes);

  attrs.forEach((attr) => {
    if (!validAttributes.includes(attr.name)) {
      errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <${element.tagName}>`,
        element: element.tagName,
        attribute: attr.name,
      });
    }
  });

  return errors;
};

/**
 * Validate <page> element attributes
 */
export const validatePageAttributes = (element: Element): ValidationError[] => {
  const errors: ValidationError[] = [];
  const attrs = Array.from(element.attributes);

  attrs.forEach((attr) => {
    if (!isValidPageAttribute(attr.name)) {
      errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <page>`,
        element: 'page',
        attribute: attr.name,
      });
    }
  });

  return errors;
};

/**
 * Validate <section> element attributes when used as root
 */
export const validateSectionRootAttributes = (element: Element): ValidationError[] => {
  const errors: ValidationError[] = [];
  const attrs = Array.from(element.attributes);

  attrs.forEach((attr) => {
    if (!isValidContainerAttribute(attr.name)) {
      errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <section>`,
        element: 'section',
        attribute: attr.name,
      });
    }
  });

  return errors;
};
